// M3.4.5 — multi-tier currency (DAoC-style).
//
// Storage: a single integer `copper` total. Display layer decomposes it on
// demand into platinum/gold/silver/copper. This avoids carry/borrow logic on
// every gain and keeps arithmetic simple.
//
// Conversion ratios (DAoC):
//   100 copper   = 1 silver
//   100 silver   = 1 gold     (= 10 000 copper)
//   1000 gold    = 1 platinum (= 10 000 000 copper)

export const COPPER_PER_SILVER = 100;
export const COPPER_PER_GOLD = COPPER_PER_SILVER * 100;        // 10_000
export const COPPER_PER_PLATINUM = COPPER_PER_GOLD * 1000;     // 10_000_000

export interface CurrencyDecomposition {
  platinum: number;
  gold: number;
  silver: number;
  copper: number;
}

export function decomposeCopper(total: number): CurrencyDecomposition {
  const t = Math.max(0, Math.floor(total));
  const platinum = Math.floor(t / COPPER_PER_PLATINUM);
  let rem = t - platinum * COPPER_PER_PLATINUM;
  const gold = Math.floor(rem / COPPER_PER_GOLD);
  rem -= gold * COPPER_PER_GOLD;
  const silver = Math.floor(rem / COPPER_PER_SILVER);
  rem -= silver * COPPER_PER_SILVER;
  const copper = rem;
  return { platinum, gold, silver, copper };
}

// "1p 234g 56s 78c" — drops zero leading denominations but always shows at
// least the smallest non-zero unit (or "0c" if total is zero).
export function formatCurrency(total: number): string {
  if (total <= 0) return "0c";
  const { platinum, gold, silver, copper } = decomposeCopper(total);
  const parts: string[] = [];
  if (platinum > 0) parts.push(`${platinum}p`);
  if (gold > 0) parts.push(`${gold}g`);
  if (silver > 0) parts.push(`${silver}s`);
  if (copper > 0) parts.push(`${copper}c`);
  return parts.join(" ");
}

// M5.1: vendor sell-back math. floor(template.value * multiplier).
// Affixes on rare instances do NOT modify the sell-back price for M5.1.
export function sellPriceOf(value: number, sellMultiplier: number): number {
  return Math.max(0, Math.floor(value * sellMultiplier));
}

// Per-buy-line qty cap for non-stackables. Stackables use the template's own
// maxStack (typically 99). The real ceiling is always copper + weight.
export const VENDOR_BUY_MAX_PER_LINE = 99;
